/**
 * ProjectThemeSection - Accordion section for project page showing active V2 theme.
 *
 * Displays:
 * - Empty state with "Add Theme" button when no theme attached
 * - Active theme preview with Change/Edit/Remove buttons
 *
 * Reuses: ThemeV2PreviewCarousel for screen previews
 *
 * @module themes/components
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Typography,
  Button,
  Box,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PaletteIcon from '@mui/icons-material/Palette';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { SAccordion } from '@modules/ui-components/dialogs/SAccordion';
import {
  DEFAULT_ACTION_BAR,
  DEFAULT_BACKDROP,
  DEFAULT_HEADER_BAR,
  type CreateThemeV2Input,
  type ThemeV2,
} from '../types/themesV2.types';
import type { ProjectThemeV2 } from '@modules/firebase-core/services/ProjectThemeService';
import { defaultThemeV2Mapper } from '@modules/themes/mappers/DefaultThemeV2Mapper';
import { remapGeneratedThemeForSave } from '@modules/themes/mappers/remapGeneratedTheme';
import { themeV2ToOnboardingSettings } from '@modules/themes/utils';
import { createDefaultFlowDocument } from '@modules/themes/factories';
import { parseThemeImportFileAsV2 } from '@modules/onboardings/services/themeSettingsIOV2';
import { ProjectThemePickerDialog } from './ProjectThemePickerDialog';
import { ThemeV2PreviewCarousel } from './ThemeV2PreviewCarousel';
import { ThemeV2EditorDialog } from './ThemeV2EditorDialog';
import { GenerateThemeFromImageDialog } from './GenerateThemeFromImageDialog';
import { GeneratedThemeMetadataPanel } from '@modules/themes/components/GeneratedThemeMetadataPanel';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_ICON_BUTTON_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from '@modules/themes/components/fullscreenDialogChrome';

export interface ProjectThemeSectionProps {
  /** Active project theme (null if none) */
  activeTheme: ProjectThemeV2 | null;
  /** Project id for generated theme analysis */
  projectId?: string;
  /** All project themes, used to show generated non-active drafts */
  themes?: ProjectThemeV2[];
  /** Loading state */
  loading: boolean;
  /** Callback when user selects a theme from global list */
  onAddTheme: (theme: ThemeV2) => Promise<unknown>;
  /** Callback when user removes the theme */
  onRemoveTheme: (themeId: string) => Promise<void>;
  /** Callback when theme is updated (from editor) */
  onUpdateTheme?: (themeId: string, data: Partial<OnboardingThemeSettingsV2>) => Promise<void>;
  /** Callback when a generated draft should be stored without activation */
  onCreateGeneratedTheme?: (theme: CreateThemeV2Input) => Promise<ProjectThemeV2>;
  /** Callback when a stored generated draft should become active */
  onSetActiveTheme?: (themeId: string) => Promise<void>;
  /** Render without SAccordion wrapper (for embedding inside another section) */
  inline?: boolean;
}

interface StatusMessage {
  text: string;
  severity: 'success' | 'error' | 'info';
}

function projectThemeToThemeV2(theme: ProjectThemeV2 | null): ThemeV2 | null {
  if (!theme) return null;
  const flowDocument = theme.flowDocument ?? createDefaultFlowDocument();
  return {
    id: theme.id,
    name: theme.name,
    colors: theme.colors,
    fonts: theme.fonts,
    optionsListSettings: theme.optionsListSettings,
    layoutSettings: theme.layoutSettings,
    background: theme.background,
    headerBar: theme.headerBar,
    actionBar: theme.actionBar,
    generatedMetadata: theme.generatedMetadata,
    flowDocument,
    createdAt: theme.createdAt,
    updatedAt: theme.updatedAt,
  };
}

function buildImportedThemeCandidate(
  importedSettings: OnboardingThemeSettingsV2,
  themeName: string
): CreateThemeV2Input {
  const flowDocument = importedSettings.flowDocument ?? createDefaultFlowDocument();
  const theme: ThemeV2 = {
    id: `imported-${Date.now()}`,
    name: themeName,
    colors: importedSettings.colors,
    fonts: importedSettings.fonts,
    optionsListSettings: importedSettings.optionsListSettings,
    layoutSettings: importedSettings.layoutSettings,
    background: importedSettings.background ?? DEFAULT_BACKDROP,
    headerBar: importedSettings.headerBar ?? DEFAULT_HEADER_BAR,
    actionBar: importedSettings.actionBar ?? DEFAULT_ACTION_BAR,
    generatedMetadata: importedSettings.generatedMetadata,
    flowDocument,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const mappedFlowDocument = defaultThemeV2Mapper.applyToFlowDocument(theme, flowDocument);

  return {
    name: themeName,
    colors: importedSettings.colors,
    fonts: importedSettings.fonts,
    optionsListSettings: importedSettings.optionsListSettings,
    layoutSettings: importedSettings.layoutSettings,
    background: importedSettings.background ?? DEFAULT_BACKDROP,
    headerBar: importedSettings.headerBar ?? DEFAULT_HEADER_BAR,
    actionBar: importedSettings.actionBar ?? DEFAULT_ACTION_BAR,
    generatedMetadata: importedSettings.generatedMetadata,
    flowDocument: mappedFlowDocument,
  };
}

function themeSettingsFromTheme(
  theme: ThemeV2,
  options: { remap?: boolean } = {}
): Partial<OnboardingThemeSettingsV2> {
  const themeToSave = options.remap ? remapGeneratedThemeForSave(theme) : theme;
  return themeV2ToOnboardingSettings(themeToSave);
}

interface ProjectThemesManagerDialogProps {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  themes: ProjectThemeV2[];
  importingTheme: boolean;
  applyingThemeId: string | null;
  onAddTheme: () => void;
  onImportTheme: () => void;
  onGenerateTheme: () => void;
  canImportTheme: boolean;
  canGenerateTheme: boolean;
  onSetActiveTheme?: (themeId: string) => void;
  onEditTheme?: (themeId: string) => void;
  onRemoveTheme: (themeId: string) => void;
}

const ProjectThemesManagerDialog: React.FC<ProjectThemesManagerDialogProps> = ({
  open,
  onClose,
  projectId,
  themes,
  importingTheme,
  applyingThemeId,
  onAddTheme,
  onImportTheme,
  onGenerateTheme,
  canImportTheme,
  canGenerateTheme,
  onSetActiveTheme,
  onEditTheme,
  onRemoveTheme,
}) => {
  const muiTheme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={FULLSCREEN_DIALOG_PAPER_SX}
      data-testid="project-theme-manager-dialog"
    >
      <Box component="header" sx={FULLSCREEN_DIALOG_HEADER_SX}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', zIndex: 1 }}>
          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close dialog"
              sx={FULLSCREEN_DIALOG_ICON_BUTTON_SX}
              data-testid="project-theme-manager-close-btn"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h6" component="h2" noWrap sx={FULLSCREEN_DIALOG_TITLE_SX}>
          Product themes
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
          sx={{ ml: 'auto', zIndex: 1 }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddTheme}
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
            data-testid="project-theme-list-add-btn"
          >
            Add
          </Button>
          {canImportTheme && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={onImportTheme}
              disabled={importingTheme}
              sx={FULLSCREEN_DIALOG_BUTTON_SX}
              data-testid="project-theme-list-import-btn"
            >
              {importingTheme ? 'Importing...' : 'Import JSON'}
            </Button>
          )}
          {canGenerateTheme && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={onGenerateTheme}
              sx={FULLSCREEN_DIALOG_BUTTON_SX}
              data-testid="project-theme-list-generate-btn"
            >
              Generate
            </Button>
          )}
        </Stack>
      </Box>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        {themes.length === 0 ? (
          <Box
            data-testid="project-theme-empty-state"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 5,
            }}
          >
            <PaletteIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography color="text.secondary" textAlign="center">
              No project themes yet. Add a global theme or import a V2 theme JSON.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddTheme}
              data-testid="project-theme-list-empty-add-btn"
            >
              Add theme
            </Button>
          </Box>
        ) : (
          <Stack spacing={2} data-testid="project-theme-list">
            {themes.map((projectTheme) => {
              const theme = projectThemeToThemeV2(projectTheme);
              if (!theme) return null;
              const isApplying = applyingThemeId === projectTheme.id;

              return (
                <Box
                  key={projectTheme.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    border: projectTheme.isActive
                      ? `2px solid ${muiTheme.palette.primary.main}`
                      : `1px solid ${muiTheme.palette.divider}`,
                    bgcolor: muiTheme.palette.background.paper,
                    minHeight: 360,
                    overflow: 'hidden',
                    minWidth: 0,
                  }}
                  data-testid={`project-theme-list-card-${projectTheme.id}`}
                  data-theme-status={projectTheme.isActive ? 'active' : 'inactive'}
                >
                  <Checkbox
                    checked={projectTheme.isActive}
                    onChange={() => {
                      if (!projectTheme.isActive && onSetActiveTheme) {
                        onSetActiveTheme(projectTheme.id);
                      }
                    }}
                    disabled={projectTheme.isActive || isApplying || !onSetActiveTheme}
                    data-testid={`project-theme-list-select-${projectTheme.id}`}
                    sx={{
                      alignSelf: 'center',
                      flexShrink: 0,
                    }}
                  />

                  <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {projectTheme.name}
                        </Typography>
                        <Chip
                          size="small"
                          label="Product"
                          color="primary"
                          data-testid={`project-theme-list-status-${projectTheme.id}`}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexShrink={0}>
                        <Tooltip title={projectTheme.isActive ? 'Active theme' : 'Set active'}>
                          <span>
                            <Checkbox
                              checked={projectTheme.isActive}
                              onChange={() => {
                                if (!projectTheme.isActive && onSetActiveTheme) {
                                  onSetActiveTheme(projectTheme.id);
                                }
                              }}
                              disabled={projectTheme.isActive || isApplying || !onSetActiveTheme}
                              size="small"
                              data-testid={`project-theme-list-set-active-${projectTheme.id}`}
                              sx={{ p: 0.75 }}
                            />
                          </span>
                        </Tooltip>
                        {onEditTheme && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => onEditTheme(projectTheme.id)}
                              data-testid={`project-theme-list-edit-${projectTheme.id}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveTheme(projectTheme.id)}
                            data-testid={`project-theme-list-remove-${projectTheme.id}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Box
                      data-testid={`project-theme-list-preview-${projectTheme.id}`}
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        height: 310,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        bgcolor: 'background.default',
                      }}
                    >
                      <ThemeV2PreviewCarousel theme={theme} compact projectId={projectId} />
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Accordion section on product page for managing project V2 theme.
 */
export const ProjectThemeSection: React.FC<ProjectThemeSectionProps> = ({
  activeTheme,
  projectId,
  themes = [],
  loading,
  onAddTheme,
  onRemoveTheme,
  onUpdateTheme,
  onCreateGeneratedTheme,
  onSetActiveTheme,
  inline = false,
}) => {
  const muiTheme = useTheme();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [applyingDraftId, setApplyingDraftId] = useState<string | null>(null);
  const [importingTheme, setImportingTheme] = useState(false);

  const editingDraft = useMemo(
    () => themes.find((theme) => theme.id === editingDraftId) ?? null,
    [editingDraftId, themes]
  );
  const projectThemeCards = useMemo(() => themes, [themes]);

  const handleOpenManager = useCallback(() => {
    if (projectThemeCards.length === 0) {
      setStatusMessage(null);
      setPickerOpen(true);
      return;
    }
    setManagerOpen(true);
  }, [projectThemeCards.length]);
  const handleCloseManager = useCallback(() => setManagerOpen(false), []);
  const handleOpenPicker = useCallback(() => {
    setStatusMessage(null);
    setPickerOpen(true);
  }, []);
  const handleClosePicker = useCallback(() => setPickerOpen(false), []);

  const handleThemeSelected = useCallback(
    async (theme: ThemeV2) => {
      await onAddTheme(theme);
      setPickerOpen(false);
    },
    [onAddTheme]
  );

  const handleCloseEditor = useCallback(() => setEditorOpen(false), []);
  const handleOpenGenerate = useCallback(() => {
    setStatusMessage(null);
    setGenerateOpen(true);
  }, []);
  const handleCloseGenerate = useCallback(() => setGenerateOpen(false), []);
  const handleImportThemeClick = useCallback(() => {
    setStatusMessage(null);
    importInputRef.current?.click();
  }, []);
  const handleImportThemeFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!onCreateGeneratedTheme) {
        setStatusMessage({ text: 'Theme import is not configured', severity: 'error' });
        return;
      }

      setImportingTheme(true);
      try {
        const importedSettings = parseThemeImportFileAsV2(await file.text());
        const cleanName = file.name
          .replace(/\.json$/i, '')
          .replace(/[_-]\d{4}-\d{2}-\d{2}.*$/i, '')
          .replace(/_/g, ' ')
          .trim();
        const themeName = `Imported: ${cleanName || 'Theme'}`;
        const created = await onCreateGeneratedTheme(
          buildImportedThemeCandidate(importedSettings, themeName)
        );
        setStatusMessage({
          text: `Theme "${created.name}" imported as inactive`,
          severity: 'success',
        });
      } catch (err) {
        setStatusMessage({
          text: err instanceof Error ? err.message : 'Failed to import theme JSON',
          severity: 'error',
        });
      } finally {
        setImportingTheme(false);
      }
    },
    [onCreateGeneratedTheme]
  );
  const handleOpenDraftEditor = useCallback((themeId: string) => setEditingDraftId(themeId), []);
  const handleCloseDraftEditor = useCallback(() => setEditingDraftId(null), []);
  const handleEditProjectTheme = useCallback((themeId: string) => {
    const projectTheme = themes.find((theme) => theme.id === themeId);
    if (!projectTheme) return;
    if (projectTheme.isActive || projectTheme.id === activeTheme?.id) {
      setEditorOpen(true);
      return;
    }
    setEditingDraftId(themeId);
  }, [activeTheme?.id, themes]);

  // Convert ProjectThemeV2 → ThemeV2 for editor (add missing ThemeV2 fields)
  const editorTheme = useMemo(() => projectThemeToThemeV2(activeTheme), [activeTheme]);
  const draftEditorTheme = useMemo(() => projectThemeToThemeV2(editingDraft), [editingDraft]);

  const handleSaveTheme = useCallback(
    async (updatedTheme: ThemeV2) => {
      if (!activeTheme || !onUpdateTheme) return;
      await onUpdateTheme(activeTheme.id, themeSettingsFromTheme(updatedTheme, {
        remap: updatedTheme.generatedMetadata?.sourceKind === 'image-analysis',
      }));
    },
    [activeTheme, onUpdateTheme]
  );

  const handleSaveDraftTheme = useCallback(
    async (updatedTheme: ThemeV2) => {
      if (!editingDraft || !onUpdateTheme) return;
      await onUpdateTheme(editingDraft.id, themeSettingsFromTheme(updatedTheme));
      setStatusMessage({ text: `Theme "${updatedTheme.name}" saved`, severity: 'success' });
    },
    [editingDraft, onUpdateTheme]
  );

  const handleCreateGeneratedTheme = useCallback(
    async (theme: CreateThemeV2Input) => {
      if (!onCreateGeneratedTheme) {
        throw new Error('Generated theme creation is not configured');
      }
      const created = await onCreateGeneratedTheme(theme);
      setStatusMessage({ text: `Draft "${created.name}" created`, severity: 'success' });
      return created;
    },
    [onCreateGeneratedTheme]
  );

  const applyDraft = useCallback(
    async (themeId: string, currentTheme?: ThemeV2) => {
      if (!onSetActiveTheme) {
        throw new Error('Generated draft activation is not configured');
      }
      setApplyingDraftId(themeId);
      try {
        const themeToApply = currentTheme ?? projectThemeToThemeV2(
          themes.find((theme) => theme.id === themeId) ?? null
        );
        if (themeToApply && onUpdateTheme) {
          await onUpdateTheme(themeId, themeSettingsFromTheme(themeToApply, {
            remap: themeToApply.generatedMetadata?.sourceKind === 'image-analysis',
          }));
        }
        await onSetActiveTheme(themeId);
        setStatusMessage({ text: 'Theme activated', severity: 'success' });
      } finally {
        setApplyingDraftId(null);
      }
    },
    [onSetActiveTheme, onUpdateTheme, themes]
  );

  const handleApplyDraft = useCallback(
    async (themeId: string) => {
      try {
        await applyDraft(themeId);
      } catch (err) {
        setStatusMessage({
          text: err instanceof Error ? err.message : 'Failed to apply generated draft',
          severity: 'error',
        });
      }
    },
    [applyDraft]
  );

  const handleApplyEditedDraft = useCallback(
    async (updatedTheme: ThemeV2) => {
      if (!editingDraft) return;
      try {
        await applyDraft(editingDraft.id, updatedTheme);
      } catch (err) {
        setStatusMessage({
          text: err instanceof Error ? err.message : 'Failed to apply generated draft',
          severity: 'error',
        });
        throw err;
      }
    },
    [applyDraft, editingDraft]
  );

  const themeContent = (
        <Box data-testid="project-theme-section" sx={{ minWidth: 0, maxWidth: '100%' }}>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={handleImportThemeFileChange}
            data-testid="project-theme-import-input"
          />
          {statusMessage && (
            <Box sx={{ mb: 2 }}>
              <Chip
                size="small"
                color={statusMessage.severity === 'error' ? 'error' : 'success'}
                icon={statusMessage.severity === 'error' ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
                label={statusMessage.text}
                data-testid="project-theme-status-chip"
              />
            </Box>
          )}

          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading theme...
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
                sx={{ minWidth: 0, maxWidth: '100%' }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PaletteIcon />}
                  onClick={handleOpenManager}
                  data-testid="project-theme-manager-btn"
                >
                  Themes
                </Button>
              </Stack>

              {activeTheme && editorTheme ? (
                <Box
                  data-testid="project-theme-active-card"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    bgcolor: muiTheme.palette.background.paper,
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      data-testid="project-theme-active-name"
                    >
                      {activeTheme.name}
                    </Typography>
                    <Chip size="small" label="Product" color="primary" />
                  </Stack>
                  <Box
                    data-testid="project-theme-active-preview"
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      height: 320,
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      bgcolor: 'background.default',
                    }}
                  >
                    <ThemeV2PreviewCarousel theme={editorTheme} compact projectId={projectId} />
                  </Box>
                </Box>
              ) : (
                <Box
                  data-testid="project-theme-empty-state"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    py: 4,
                    border: `1px dashed ${muiTheme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <PaletteIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {projectThemeCards.length > 0
                      ? 'No active theme selected. Open Themes to choose a project theme.'
                      : 'No theme attached. Open Themes to add or import a V2 theme.'}
                  </Typography>
                  {projectThemeCards.length === 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleOpenPicker}
                      data-testid="project-theme-empty-add-btn"
                    >
                      Add theme
                    </Button>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Box>
  );

  return (
    <>
      {inline ? themeContent : (
        <SAccordion title="Theme V2" collapsible defaultCollapsed={!activeTheme}>
          {themeContent}
        </SAccordion>
      )}

      <ProjectThemesManagerDialog
        open={managerOpen}
        onClose={handleCloseManager}
        projectId={projectId}
        themes={projectThemeCards}
        importingTheme={importingTheme}
        applyingThemeId={applyingDraftId}
        onAddTheme={handleOpenPicker}
        onImportTheme={handleImportThemeClick}
        onGenerateTheme={handleOpenGenerate}
        canImportTheme={Boolean(onCreateGeneratedTheme)}
        canGenerateTheme={Boolean(projectId && onCreateGeneratedTheme)}
        onSetActiveTheme={onSetActiveTheme ? handleApplyDraft : undefined}
        onEditTheme={onUpdateTheme ? handleEditProjectTheme : undefined}
        onRemoveTheme={onRemoveTheme}
      />

      {/* Theme picker dialog */}
      <ProjectThemePickerDialog
        open={pickerOpen}
        onClose={handleClosePicker}
        onSelect={handleThemeSelected}
        projectId={projectId}
      />

      {/* Theme editor dialog */}
      <ThemeV2EditorDialog
        open={editorOpen}
        theme={editorTheme}
        projectId={projectId}
        title="Edit Project Theme"
        onClose={handleCloseEditor}
        onSave={handleSaveTheme}
        metadataPanel={editorTheme?.generatedMetadata?.sourceKind === 'image-analysis'
          ? <GeneratedThemeMetadataPanel theme={editorTheme} />
          : undefined}
      />

      <ThemeV2EditorDialog
        open={Boolean(editingDraftId)}
        theme={draftEditorTheme}
        projectId={projectId}
        title={draftEditorTheme?.generatedMetadata?.sourceKind === 'image-analysis'
          ? 'Edit Generated Theme'
          : 'Edit Project Theme'}
        onClose={handleCloseDraftEditor}
        onSave={handleSaveDraftTheme}
        onApply={onSetActiveTheme ? handleApplyEditedDraft : undefined}
        saveLabel="Save Theme"
        savingLabel="Saving Theme..."
        applyLabel="Set Active"
        applyingLabel="Applying..."
        metadataPanel={draftEditorTheme ? <GeneratedThemeMetadataPanel theme={draftEditorTheme} /> : undefined}
      />

      {projectId && onCreateGeneratedTheme && (
        <GenerateThemeFromImageDialog
          open={generateOpen}
          projectId={projectId}
          baseTheme={editorTheme}
          createActionLabel="Create Draft"
          onClose={handleCloseGenerate}
          onCreateTheme={handleCreateGeneratedTheme}
        />
      )}
    </>
  );
};
